import React from 'react'
import {createContext, ReactNode, useCallback, useMemo, useRef} from 'react'
import axios, {AxiosError, AxiosInstance} from 'axios'
import {useFeedback} from "../hooks/useFeedback"
import {API_HOST} from "../constants"
import {auth} from "../firebase"
import {useCookies} from "react-cookie"


export const IApiServiceContext = createContext<ApiServiceContextState>({})

export type ApiServiceContextState = {
    client?: (withAuth?: boolean, handleFeedback?: boolean) => Promise<AxiosInstance>
}

const ApiServiceContext = (props: { children: ReactNode }) => {
    const [, , removeCookie] = useCookies(['scorevision_user'])
    const feedback = useFeedback()

    /**
     * Read through refs so `client` never has to be rebuilt.
     *
     * Its identity is a dependency of everything that loads data, and this provider re-renders on
     * every cookie write: rebuilding it there restarted those loaders, which wrote a cookie again.
     */
    const feedbackRef = useRef(feedback)
    const removeCookieRef = useRef(removeCookie)
    feedbackRef.current = feedback
    removeCookieRef.current = removeCookie

    const client = useCallback((withAuth: boolean, handleFeedback?: boolean): Promise<AxiosInstance> =>
        new Promise<AxiosInstance>(async (resolve, reject) => {
                const client = axios.create({
                    baseURL: API_HOST
                })

                // Attach interceptor once
                client.interceptors.response.use(
                    (response) => response,

                    (error: AxiosError) => {
                        console.log(JSON.stringify(error.response?.data?.['message']))
                        if (handleFeedback) {
                            const msg = error.response?.data?.['message']
                                || 'An unknown error has occurred, please try again'
                            feedbackRef.current.showFeedback(msg, 'danger')
                        }
                        return Promise.reject(error)
                    }
                )
            if (withAuth) {
                /**
                 * Resolved from the first auth state, then unsubscribed: leaving the listener
                 * attached leaks one per request, and a signed out visitor used to leave the
                 * promise pending forever, freezing every screen that asks for a client.
                 *
                 * The flag carries the unsubscribing when the listener answers synchronously,
                 * before there is a handle to unsubscribe with.
                 */
                let unsubscribe: (() => void) | null = null
                let settled = false

                unsubscribe = auth?.onAuthStateChanged(async (value) => {
                    if (settled) return
                    settled = true
                    unsubscribe?.()

                    if (!value) {
                        removeCookieRef.current('scorevision_user')
                        resolve(client)
                        return
                    }

                    client.defaults.headers.common['Authorization'] = await value.getIdToken()
                    resolve(client)
                })

                if (settled) unsubscribe?.()
            } else
                resolve(client)
            }
        ), [])

    const state = useMemo<ApiServiceContextState>(() => ({client}), [client])

    return <IApiServiceContext.Provider value={state}>
        {props.children}
    </IApiServiceContext.Provider>
}

export default ApiServiceContext
